<template>
  <section class="pumping-protection" aria-labelledby="protection-title">
    <div class="protection-heading">
      <div>
        <h1 id="protection-title">Pumping Protection</h1>
        <p class="protection-description">Review outgoing SMS and identify messages that need attention.</p>
      </div>
      <button class="btn protection-secondary" :disabled="loading" @click="load">Refresh</button>
    </div>
    <div class="protection-status">
      <span class="protection-status-label">
        <mdicon name="shield-check-outline" :size="20" aria-hidden="true" />
        {{ statusLabel }}
      </span>
      <span>Review proposed decisions without blocking or resending messages.</span>
    </div>
    <div v-if="error" role="alert" class="protection-empty">
      <mdicon name="message-text-outline" :size="32" aria-hidden="true" />
      <h2>{{ unavailable ? 'Observation logging is not available yet' : 'Unable to load observations' }}</h2>
      <p>{{ error }}</p>
      <p v-if="unavailable" class="protection-caption">
        SMS sending is unchanged. Observations will appear here once logging is connected.
      </p>
    </div>
    <BCard v-if="result?.volumeModel" class="mb-4">
      <h2 class="h4">Volume follows shipments</h2>
      <p>Expected SMS = historical SMS per shipment × current shipment volume × a safety margin.</p>
      <p>
        Calculated globally and per customer using booked proxy shipments. The last seven days also provide allowance
        for delayed notifications.
      </p>
      <p v-if="result.volumeModel.status === 'ready'" class="mb-0">
        Reviewed baseline: {{ result.volumeModel.baselineFrom }}–{{ result.volumeModel.baselineTo }} (UTC). Current
        global allowance: {{ result.volumeModel.limits['global_day'] }} SMS / 24 hours. This grows with shipment volume.
      </p>
      <p v-else class="mb-0">
        {{
          result.volumeModel.status === 'data_unavailable'
            ? 'Shipment or baseline data is unavailable.'
            : 'Volume comparison awaits a reviewed baseline with sufficient history.'
        }}
        No fixed business-volume ceiling is substituted. Duplicate, per-recipient and per-message checks remain
        separate.
      </p>
    </BCard>
    <p v-if="!connected" role="status">Live updates are disconnected. Displayed data may be outdated; refresh to check again.</p>
    <TwilioComparison />
    <BCard class="filter protection-filters">
      <h3>Filter observations</h3>
      <form class="protection-filter-grid" @submit.prevent="applyFilters">
        <label>From ({{ result?.timezone || 'Europe/Stockholm' }})<input v-model="from" type="date" class="form-control" /></label>
        <label>To ({{ result?.timezone || 'Europe/Stockholm' }})<input v-model="to" type="date" class="form-control" /></label>
        <label>Decision
          <select v-model="decision" class="form-select">
            <option value="">All decisions</option>
            <option value="allow">Would allow</option>
            <option value="would_pause">Would pause for review</option>
            <option value="would_block">Would block</option>
          </select>
        </label>
        <label>Shipment<input v-model="shipment" class="form-control" maxlength="100" /></label>
        <button class="btn btn-green" :disabled="loading">Apply filters</button>
      </form>
    </BCard>
    <p v-if="loading" role="status">Loading observations…</p>
    <template v-if="result">
      <p class="protection-caption">
        Last recorded attempt ({{ result.timezone }}): {{ result.lastObservationAt || 'None' }}. Missing observations and unknown provider
        outcomes require investigation before enabling blocking.
      </p>
      <div class="row g-3 mb-3">
        <div v-for="summary in result.summary" :key="summary.decision" class="col-md-4">
          <div class="card protection-metric">
            <h2 class="h6">{{ labels[summary.decision] }}</h2>
            <strong>{{ summary.attempts }} attempts</strong>
            <span>{{ protectionMoney(summary.estimatedCostUsd) }} estimated traffic value</span>
            <small>{{ summary.unpricedAttempts }} attempts without a known tariff</small>
          </div>
        </div>
      </div>
      <p>
        Estimated traffic value is not money saved. Messages still go to Twilio; its fees and decisions still apply.
      </p>
      <details class="card protection-rules">
        <summary>Provisional rules · {{ result.rules.version }}</summary>
        <p class="mt-2">Limits use rolling windows. Duplicate window: {{ result.rules.duplicateSeconds }} seconds.</p>
        <dl class="protection-rule-list">
          <div v-for="(limit, rule) in result.rules.limits" :key="rule">
            <dt>{{ protectionLimitLabel(rule) }}</dt>
            <dd>{{ limit }}</dd>
          </div>
          <div>
            <dt>Allowed countries</dt>
            <dd>{{ result.rules.allowedCountries.join(', ') || 'All destinations' }}</dd>
          </div>
        </dl>
      </details>
      <EasyDataTable
        ref="dataTable"
        v-model:server-options="serverOptions"
        :server-items-length="result.observations.count"
        :items="result.observations.data"
        :headers="headers"
        :loading="loading"
        :rows-per-page="protectionPageSize"
        :rows-items="[protectionPageSize]"
        table-class-name="vue3-easy-data-table table-shadow no-overflow"
        alternating
        hide-footer
        @update:server-options="updateServerOptions"
      >
        <template #empty-message>No observations match these filters.</template>
        <template #item-createdAt="row">
          {{ row.createdAt }}<br /><strong>{{ row.shipmentNumber || 'No shipment context' }}</strong>
        </template>
        <template #item-recipientMask="row">
          {{ row.recipientMask }}<br />{{ row.country || 'Unknown country' }}
        </template>
        <template #item-decision="row">
          <strong>{{ labels[row.decision as ProtectionDecision] }}</strong>
          <ul class="mb-1">
            <li v-for="reason in row.reasons" :key="reason">{{ protectionReasonLabel(reason, row.evidence) }}</li>
          </ul>
          <details>
            <summary>Decision evidence</summary>
            <pre>{{ JSON.stringify(row.evidence, null, 2) }}</pre>
          </details>
        </template>
        <template #item-providerStatus="row">
          {{ protectionProviderLabel(row.providerStatus) }}<br v-if="row.providerErrorCode" />{{
            row.providerErrorCode || ''
          }}
        </template>
        <template #item-estimatedCostUsd="row">
          {{ protectionMoney(row.estimatedCostUsd) }}<br />{{ row.segments }} estimated segments
        </template>
        <template #item-reviews="row">
          <span v-if="row.reviews[0]">{{ verdictLabels[row.reviews[0].verdict as ProtectionVerdict] }}</span>
          <button class="btn btn-sm protection-secondary d-block" @click="select(row)">Review attempt</button>
        </template>
      </EasyDataTable>
      <TablePagination
        v-model:server-options-ref="serverOptions"
        v-model:data-table-ref="dataTable"
        :inert="loading"
        :server-items-length="result.observations.count"
      />
    </template>
    <section v-if="selected" class="card protection-review" aria-labelledby="review-title">
      <h2 id="review-title" class="h4">Review attempt #{{ selected.id }}</h2>
      <p>Record whether the proposed decision was appropriate. Saving a review does not send or release an SMS.</p>
      <ul>
        <li v-for="review in selected.reviews" :key="review.id">
          {{ review.createdAt }} · {{ verdictLabels[review.verdict] }} · {{ review.comment }}
        </li>
      </ul>
      <form @submit.prevent="saveReview">
        <label class="d-block mb-3">Assessment
          <select v-model="verdict" class="form-select">
            <option value="uncertain">Needs investigation</option>
            <option value="correct">Proposed decision is correct</option>
            <option value="false_positive">Too aggressive — legitimate SMS</option>
          </select>
        </label>
        <label class="d-block mb-3">Reason (do not include phone numbers or message text)
          <textarea v-model="comment" class="form-control" required maxlength="1000" rows="3" />
        </label>
        <button class="btn btn-green" :disabled="!canSave">Save assessment</button>
        <button type="button" class="btn protection-secondary ml-2" :disabled="saving" @click="closeReview">
          Close
        </button>
        <p v-if="reviewError" role="alert" class="text-danger mt-2">{{ reviewError }}</p>
      </form>
    </section>
    <p v-if="saved" role="status" class="protection-status mt-3">Assessment saved. Sending behaviour is unchanged.</p>
  </section>
</template>

<script lang="ts">
import type { ProtectionResponse } from '@/types/Api/SmsProtection'
import type { ProtectionDecision, ProtectionObservation, ProtectionVerdict } from '@/types/Models/SmsProtection'
import type { ComputedRef, Ref } from 'vue'
import { isAxiosError } from 'axios'
import { BCard } from 'bootstrap-vue-next'
import { computed, defineComponent, ref } from 'vue'
import { useMessagingProtectionUpdates } from '@/composables/useMessagingProtectionUpdates'
import { protectionPageSize } from '@/const/MessagingProtection'
import {
  protectionReasonLabel,
  protectionMoney,
  protectionProviderLabel,
  protectionLimitLabel,
} from '@/utils/smsProtection'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import TwilioComparison from '@/views/Staff/PumpingProtection/TwilioComparison.vue'
import SmsProtectionProxy from '@/proxies/SmsProtectionProxy'

export default defineComponent({
  name: 'PumpingProtectionIndex',
  components: { BCard, TwilioComparison, EasyDataTable, TablePagination },
  setup() {
    const dataTable: Ref<InstanceType<typeof EasyDataTable> | null> = ref(null)
    const serverOptions: Ref<ServerOptions> = ref({ page: 1, rowsPerPage: protectionPageSize })
    const loading: Ref<boolean> = ref(false)
    const error: Ref<string> = ref('')

    const result: Ref<ProtectionResponse | null> = ref(null)
    const unavailable: Ref<boolean> = ref(false)
    const from: Ref<string> = ref('')
    const to: Ref<string> = ref('')
    const decision: Ref<ProtectionDecision | ''> = ref('')
    const shipment: Ref<string> = ref('')
    let appliedFilters = { from: '', to: '', decision: '' as ProtectionDecision | '', shipmentNumber: '' }
    const selected: Ref<ProtectionObservation | null> = ref(null)
    const verdict: Ref<ProtectionVerdict> = ref('uncertain')
    const comment: Ref<string> = ref('')
    const saving: Ref<boolean> = ref(false)
    const saved: Ref<boolean> = ref(false)
    const reviewError: Ref<string> = ref('')
    const statusLabel: ComputedRef<string> = computed(() => {
      if (loading.value) return 'Checking status…'
      if (result.value?.mode === 'observe') return 'Observation mode'
      return result.value?.mode === 'disabled' ? 'Observation inactive' : 'Awaiting observations'
    })
    const canSave: ComputedRef<boolean> = computed(() => Boolean(!saving.value && comment.value.trim()))
    const labels: Record<ProtectionDecision, string> = {
      allow: 'Would allow',
      would_pause: 'Would pause for review',
      would_block: 'Would block',
    }
    const verdictLabels: Record<ProtectionVerdict, string> = {
      correct: 'Correct',
      false_positive: 'Too aggressive',
      uncertain: 'Needs investigation',
    }
    const headers: Header[] = [
      { text: 'Time / shipment', value: 'createdAt' },
      { text: 'Destination', value: 'recipientMask' },
      { text: 'Proposed decision', value: 'decision' },
      { text: 'Provider result', value: 'providerStatus' },
      { text: 'Estimated cost', value: 'estimatedCostUsd' },
      { text: 'Review', value: 'reviews' },
    ]

    const load = async (): Promise<void> => {
      if (loading.value) return
      loading.value = true
      error.value = ''
      unavailable.value = false
      try {
        result.value = await new SmsProtectionProxy().observations({
          page: serverOptions.value.page,
          ...appliedFilters,
        })
        if (!from.value) from.value = result.value.from
        if (!to.value) to.value = result.value.to
      } catch (cause) {
        result.value = null
        unavailable.value = isAxiosError(cause) && [404, 503].includes(cause.response?.status || 0)
        error.value = unavailable.value
          ? 'Observation logging is not available in this environment yet.'
          : 'Could not load protection observations. Check your access and try again.'
      } finally {
        loading.value = false
      }
    }

    const applyFilters = (): void => {
      appliedFilters = { from: from.value, to: to.value, decision: decision.value, shipmentNumber: shipment.value }
      serverOptions.value = { ...serverOptions.value, page: 1 }
      void refresh()
    }

    const updateServerOptions = (options: ServerOptions): void => {
      serverOptions.value = options
      void refresh()
    }

    const select = (row: ProtectionObservation): void => {
      selected.value = row
      verdict.value = 'uncertain'
      comment.value = ''
      reviewError.value = ''
      saved.value = false
    }

    const closeReview = (): void => {
      selected.value = null
      reviewError.value = ''
    }

    const saveReview = async (): Promise<void> => {
      if (!selected.value || !canSave.value) return
      saving.value = true
      reviewError.value = ''
      try {
        await new SmsProtectionProxy().review(selected.value.id, { verdict: verdict.value, comment: comment.value })
        closeReview()
        saved.value = true
        await refresh()
      } catch {
        reviewError.value = 'Assessment could not be saved. Your draft has been kept.'
      } finally {
        saving.value = false
      }
    }

    const { connected, refresh } = useMessagingProtectionUpdates('evaluation', load)

    return {
      result,
      loading,
      error,
      unavailable,
      statusLabel,
      from,
      to,
      decision,
      shipment,
      selected,
      verdict,
      comment,
      saving,
      saved,
      reviewError,
      labels,
      verdictLabels,
      headers,
      dataTable,
      serverOptions,
      protectionPageSize,
      canSave,
      load: refresh,
      connected,
      applyFilters,
      updateServerOptions,
      select,
      closeReview,
      saveReview,
      protectionReasonLabel,
      protectionMoney,
      protectionProviderLabel,
      protectionLimitLabel,
    }
  },
})
</script>
